import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Pause, Play, Trash2, Zap } from "lucide-react";

import { ApiError, api } from "../api/client";
import { useAuth } from "../auth/AuthProvider";
import { Badge, Button, Card, EmptyState, PageHeader, Skeleton, TableSkeleton } from "../components/ui";
import { formatDate, formatDateTime, formatMoney } from "../lib/format";
import { isManagerOrAbove } from "../lib/people";
import { SCHEDULE_STATUS_TONE } from "../lib/recurring";
import { useAccountTimeZone } from "../lib/useAccountTimeZone";

const RUN_TONE: Record<string, "emerald" | "rose" | "slate"> = {
  generated: "emerald",
  failed: "rose",
  skipped: "slate",
};

/**
 * One recurring schedule in full: its template, the invoice it will issue next,
 * and every generation attempt. The generated invoices are links, because the
 * schedule only ever creates them -- it never rewrites one.
 */
export function RecurringScheduleDetailPage() {
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const timeZone = useAccountTimeZone();

  const query = useQuery({
    queryKey: ["billing-schedule", id],
    queryFn: ({ signal }) => api.billingSchedule(id!, signal),
    enabled: Boolean(id),
  });
  const history = useQuery({
    queryKey: ["billing-schedule", id, "history"],
    queryFn: ({ signal }) => api.billingScheduleHistory(id!, signal),
    enabled: Boolean(id),
  });
  const preview = useQuery({
    queryKey: ["billing-schedule", id, "preview"],
    queryFn: () => api.previewBillingSchedule({ schedule: id! }),
    enabled: Boolean(id) && Boolean(query.data) && query.data?.status !== "cancelled",
    retry: false,
  });

  function invalidate() {
    void queryClient.invalidateQueries({ queryKey: ["billing-schedule", id] });
    void queryClient.invalidateQueries({ queryKey: ["billing-schedule", id, "preview"] });
    void queryClient.invalidateQueries({ queryKey: ["billing-schedules"] });
  }

  const action = useMutation({
    mutationFn: (verb: "pause" | "resume" | "cancel" | "generate") => {
      if (verb === "pause") return api.pauseBillingSchedule(id!);
      if (verb === "resume") return api.resumeBillingSchedule(id!);
      if (verb === "cancel") return api.cancelBillingSchedule(id!);
      return api.generateBillingSchedule(id!);
    },
    onSuccess: invalidate,
  });

  if (query.isPending) {
    return (
      <>
        <BackLink />
        <Card>
          <Skeleton className="h-40 w-full" />
        </Card>
      </>
    );
  }
  if (query.isError || !query.data) {
    return (
      <>
        <BackLink />
        <Card>
          <EmptyState
            title="Schedule not found"
            body={
              query.error instanceof ApiError
                ? query.error.message
                : "It may belong to another account."
            }
          />
        </Card>
      </>
    );
  }

  const schedule = query.data;
  const canWrite = isManagerOrAbove(user?.role);
  const active = schedule.status === "active" || schedule.status === "paused";

  return (
    <>
      <BackLink />
      <PageHeader
        title={schedule.name}
        subtitle={schedule.contact_name || undefined}
        actions={
          <>
            <Badge tone={SCHEDULE_STATUS_TONE[schedule.status] ?? "slate"}>
              <span className="capitalize">{schedule.status}</span>
            </Badge>
            {canWrite && active ? (
              <>
                {schedule.status === "active" ? (
                  <Button variant="secondary" onClick={() => action.mutate("pause")}>
                    <Pause size={14} /> Pause
                  </Button>
                ) : (
                  <Button variant="secondary" onClick={() => action.mutate("resume")}>
                    <Play size={14} /> Resume
                  </Button>
                )}
                <Button variant="primary" onClick={() => action.mutate("generate")}>
                  <Zap size={14} /> Run now
                </Button>
              </>
            ) : null}
            {canWrite && schedule.status !== "cancelled" && schedule.status !== "completed" ? (
              <Button variant="secondary" onClick={() => action.mutate("cancel")}>
                <Trash2 size={14} /> Cancel
              </Button>
            ) : null}
          </>
        }
      />

      {action.isError ? (
        <p role="alert" className="mb-4 rounded-md bg-rose-50 px-3 py-2 text-sm text-rose-700">
          {action.error instanceof ApiError ? action.error.message : "That action failed."}
        </p>
      ) : null}

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          <Card padded={false}>
            <div className="border-b border-slate-100 px-5 py-4">
              <h2 className="text-sm font-semibold">Lines</h2>
            </div>
            {schedule.items.length === 0 ? (
              <EmptyState title="No lines" body="This schedule has nothing to bill." />
            ) : (
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-100 text-left text-xs font-medium uppercase tracking-wide text-slate-500">
                    <th className="px-5 py-2.5">Description</th>
                    <th className="px-4 py-2.5 text-right">Qty</th>
                    <th className="px-4 py-2.5 text-right">Unit price</th>
                    <th className="px-4 py-2.5 text-right">Discount</th>
                    <th className="px-4 py-2.5 text-right">Tax</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {schedule.items.map((item) => (
                    <tr key={item.id}>
                      <td className="px-5 py-3 text-sm text-slate-800">{item.description || "—"}</td>
                      <td className="px-4 py-3 text-right text-sm tabular-nums text-slate-600">
                        {item.quantity}
                      </td>
                      <td className="px-4 py-3 text-right text-sm tabular-nums text-slate-600">
                        {formatMoney(item.unit_price)}
                      </td>
                      <td className="px-4 py-3 text-right text-sm tabular-nums text-slate-600">
                        {formatMoney(item.discount_amount)}
                      </td>
                      <td className="px-4 py-3 text-right text-sm text-slate-600">
                        {item.tax ? "Yes" : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Card>

          <Card>
            <h2 className="mb-4 text-sm font-semibold">Next invoice preview</h2>
            {schedule.status === "cancelled" ? (
              <p className="text-sm text-slate-500">A cancelled schedule issues nothing further.</p>
            ) : preview.isPending ? (
              <Skeleton className="h-20 w-full" />
            ) : preview.isError || !preview.data ? (
              <p className="text-sm text-slate-500">
                {preview.error instanceof ApiError
                  ? preview.error.message
                  : "The next invoice cannot be worked out."}
              </p>
            ) : (
              <dl className="space-y-2 text-sm">
                <PreviewRow label="Issue date" value={formatDate(preview.data.issue_date, timeZone)} />
                <PreviewRow label="Due" value={formatDate(preview.data.due_on, timeZone)} />
                <PreviewRow label="Subtotal" value={formatMoney(preview.data.subtotal)} />
                <PreviewRow label="Discount" value={formatMoney(preview.data.discount_amount)} />
                <PreviewRow label="Tax" value={formatMoney(preview.data.tax_amount)} />
                <div className="border-t border-slate-100 pt-2">
                  <PreviewRow label="Total" value={formatMoney(preview.data.total_amount)} strong />
                </div>
              </dl>
            )}
          </Card>

          <Card padded={false}>
            <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4">
              <h2 className="text-sm font-semibold">Generation history</h2>
              <span className="text-xs text-slate-500">
                {history.data ? `${history.data.results.length} attempts` : "—"}
              </span>
            </div>
            {history.isPending ? (
              <TableSkeleton rows={3} />
            ) : (history.data?.results.length ?? 0) === 0 ? (
              <EmptyState
                title="Nothing generated yet"
                body="Once this schedule runs, each attempt is listed here with its invoice."
              />
            ) : (
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-100 text-left text-xs font-medium uppercase tracking-wide text-slate-500">
                    <th className="px-5 py-2.5">Run date</th>
                    <th className="px-4 py-2.5">Status</th>
                    <th className="px-4 py-2.5">Invoice</th>
                    <th className="px-5 py-2.5">Detail</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {history.data?.results.map((run) => (
                    <tr key={run.id}>
                      <td className="px-5 py-3 text-sm tabular-nums text-slate-700">
                        {formatDate(run.run_date, timeZone)}
                      </td>
                      <td className="px-4 py-3">
                        <Badge tone={RUN_TONE[run.status] ?? "slate"}>
                          <span className="capitalize">{run.status}</span>
                        </Badge>
                      </td>
                      <td className="px-4 py-3 text-sm tabular-nums">
                        {run.invoice ? (
                          <Link
                            to={`/billing/invoices/${run.invoice}`}
                            className="text-slate-700 hover:underline"
                          >
                            {run.invoice_number || "Invoice"}
                          </Link>
                        ) : (
                          <span className="text-slate-400">—</span>
                        )}
                      </td>
                      <td className="px-5 py-3 text-xs text-slate-500">{run.detail || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Card>
        </div>

        <div className="space-y-6">
          <Card>
            <h2 className="mb-4 text-sm font-semibold">Schedule</h2>
            <dl className="space-y-3 text-sm">
              <Row label="Frequency" value={frequencyLabel(schedule.frequency, schedule.interval_count)} />
              <Row label="Start" value={formatDate(schedule.start_date, timeZone)} />
              <Row label="End" value={schedule.end_date ? formatDate(schedule.end_date, timeZone) : "Open"} />
              <Row label="Next run" value={formatDate(schedule.next_invoice_date, timeZone)} />
              <Row
                label="Last run"
                value={schedule.last_generated_at ? formatDateTime(schedule.last_generated_at, timeZone) : "Never"}
              />
              <Row label="Payment terms" value={`${schedule.payment_terms_days} days`} />
              <Row label="Auto-issue" value={schedule.auto_generate ? "Yes" : "No (draft)"} />
              <Row
                label="Generated"
                value={`${schedule.occurrences_generated}${
                  schedule.total_occurrences ? ` of ${schedule.total_occurrences}` : ""
                }`}
              />
            </dl>
          </Card>

          <Card>
            <h2 className="mb-3 text-sm font-semibold">Customer</h2>
            <p className="text-sm font-medium text-slate-900">{schedule.contact_name || "—"}</p>
            <Link
              to={`/customers/contacts/${schedule.contact}`}
              className="mt-2 inline-flex text-xs font-medium text-slate-600 underline hover:text-slate-900"
            >
              View contact
            </Link>
          </Card>

          {schedule.last_invoice ? (
            <Card>
              <h2 className="mb-3 text-sm font-semibold">Last invoice</h2>
              <Link
                to={`/billing/invoices/${schedule.last_invoice.id}`}
                className="text-sm font-medium text-slate-800 hover:underline"
              >
                {schedule.last_invoice.number || "Draft"}
              </Link>
              <p className="mt-1 text-xs text-slate-500">
                {formatDate(schedule.last_invoice.issue_date, timeZone)} · {schedule.last_invoice.status}
              </p>
            </Card>
          ) : null}

          {schedule.last_error ? (
            <Card className="border-rose-200 bg-rose-50/60">
              <h2 className="mb-2 text-sm font-semibold text-rose-800">Last run failed</h2>
              <p className="whitespace-pre-wrap text-xs text-rose-700">{schedule.last_error}</p>
            </Card>
          ) : null}
        </div>
      </div>
    </>
  );
}

function frequencyLabel(frequency: string, interval: number): string {
  const label = frequency.replace(/_/g, " ");
  const capitalised = label.charAt(0).toUpperCase() + label.slice(1);
  return interval > 1 ? `${capitalised} ×${interval}` : capitalised;
}

function PreviewRow({
  label,
  value,
  strong,
}: {
  label: string;
  value: string;
  strong?: boolean;
}) {
  return (
    <div className="flex items-baseline justify-between gap-4">
      <dt className="text-slate-500">{label}</dt>
      <dd className={strong ? "font-semibold text-slate-900" : "text-slate-800"}>{value}</dd>
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between gap-4">
      <dt className="text-slate-500">{label}</dt>
      <dd className="text-right text-slate-800">{value}</dd>
    </div>
  );
}

function BackLink() {
  return (
    <Link
      to="/billing/recurring"
      className="mb-3 inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900"
    >
      <ArrowLeft size={14} /> All schedules
    </Link>
  );
}
